// ─── IBOS (Integrated Benefits Optimization Strategy) Default Layer Configuration ───
// These defaults seed the tenant-level IBOS layer config in Admin > IBOS Layers.
// Each layer defines its display content, financial model method, and compliance notes.

export const SEED_IBOS_LAYERS = [
  {
    id: 'section125',
    order: 1,
    name: 'Section 125 Cafeteria Plan',
    shortDescription: 'Pre-tax premium deduction — the core Section 125 analysis.',
    longDescription:
      'Pre-tax treatment of qualified benefit premiums. This layer pulls directly from the Workforce Section 125 analysis above and always reflects the current eligibility and optimization results.',
    icon: 'shield-checkmark-outline',
    defaultStatus: 'active', // active | available | future | na
    defaultEnabled: true,

    // Special: Layer 1 is computed live from Workforce analysis, not admin-entered
    calculationMethod: 'computed_from_section125',
    inputs: {},

    employerSavingsMethod: 'from_section125',
    employerSavingsParams: {},
    employeeValueMethod: 'from_section125',
    employeeValueParams: {},

    complianceNote: '',
    requires: { legalReview: false, stateFiling: false, erisa: false },
    bulletPoints: [
      'Pre-tax premium deduction for qualified benefits',
      'Reduces employer FICA liability',
      'Increases employee take-home pay',
    ],
    color: 'green',
  },
  {
    id: 'section105',
    order: 2,
    name: 'Section 105 HRA',
    shortDescription: 'Tax-free reimbursement of qualified medical expenses.',
    longDescription:
      'Employer-funded, deductible to employer, tax-free to employee. Reimburses deductibles, copays, prescriptions, preventive care, and wellness expenses.',
    icon: 'medkit-outline',
    defaultStatus: 'available',
    defaultEnabled: false,

    calculationMethod: 'per_employee_monthly',
    inputs: {
      monthlyContribution: 100, // $ per employee per month
      eligibleSource: 'section125_eligible', // derive from 125 eligibility
    },

    employerSavingsMethod: 'tax_deduction_value',
    employerSavingsParams: {
      taxRate: 0.272, // combined federal + state tax rate estimate
    },
    employeeValueMethod: 'utilization_adjusted',
    employeeValueParams: {
      utilizationRate: 0.4, // 40% of eligible employees use the benefit meaningfully
    },

    complianceNote: 'Requires written plan document and non-discrimination testing.',
    requires: { legalReview: true, stateFiling: false, erisa: true },
    bulletPoints: [
      'Deductibles & copays',
      'Telehealth visit costs',
      'Preventive care expenses',
      'Prescription support',
      'Wellness reimbursements',
    ],
    color: 'blue',
  },
  {
    id: 'telehealth',
    order: 3,
    name: 'Telehealth Platform',
    shortDescription: '24/7 virtual primary care, behavioral health, and family telemedicine.',
    longDescription:
      '$0 copay virtual care. Reduces ER and urgent care utilization while providing strong perceived employee value.',
    icon: 'videocam-outline',
    defaultStatus: 'active',
    defaultEnabled: true,

    calculationMethod: 'utilization_model',
    inputs: {
      visitsPerEmployeePerYear: 2.4,
      avoidedErVisitsPerYear: 12,
      avgErCost: 700, // avoided cost per ER visit
      perceivedValuePerMonth: 75, // employee-perceived value $/month
    },

    employerSavingsMethod: 'cost_avoidance',
    employerSavingsParams: {
      avoidedErVisitsPerYear: 12,
      avgErCost: 700,
    },
    employeeValueMethod: 'perceived_value',
    employeeValueParams: {
      perceivedValuePerMonth: 75,
    },

    complianceNote: '',
    requires: { legalReview: false, stateFiling: false, erisa: false },
    bulletPoints: [
      'Virtual primary care',
      'Behavioral health / teletherapy',
      'Pediatric telemedicine',
      'Health coaching',
      'AI symptom triage',
    ],
    color: 'purple',
  },
  {
    id: 'mec',
    order: 4,
    name: 'MEC / Health Share',
    shortDescription: 'ACA-compliant preventive coverage or modern health sharing alternative.',
    longDescription:
      'Provides baseline healthcare access with ACA safe harbor (MEC) or community-based cost sharing (Health Share). Used carefully to complement other layers.',
    icon: 'heart-outline',
    defaultStatus: 'available',
    defaultEnabled: false,

    calculationMethod: 'per_employee_monthly',
    inputs: {
      monthlyContribution: 50, // MEC default
      eligibleSource: 'section125_eligible',
      perceivedValuePerMonth: 30,
    },

    employerSavingsMethod: 'none',
    employerSavingsParams: {},
    employeeValueMethod: 'perceived_value',
    employeeValueParams: {
      perceivedValuePerMonth: 30,
    },

    complianceNote:
      'Health shares are NOT insurance. Must be marketed carefully with compliance oversight. MEC provides ACA safe harbor.',
    requires: { legalReview: true, stateFiling: false, erisa: false },
    bulletPoints: [
      'Preventive services',
      'Wellness exams & immunizations',
      'Optional health-share community support',
    ],
    color: 'orange',
  },
  {
    id: 'hospitalization',
    order: 5,
    name: 'Hospitalization Indemnity',
    shortDescription: 'Fixed-benefit cash payments for hospital stays, ER visits, and ambulance services.',
    longDescription:
      'Already included in Section 125 plan tiers. Provides cash benefits for hospital admissions, ER visits, and ambulance.',
    icon: 'business-outline',
    defaultStatus: 'active',
    defaultEnabled: true,

    calculationMethod: 'from_plan_tiers',
    inputs: {
      perEnrolledAnnualValue: 215, // default estimated value per enrolled employee per year
    },

    employerSavingsMethod: 'none',
    employerSavingsParams: {},
    employeeValueMethod: 'per_enrolled_fixed',
    employeeValueParams: {
      perEnrolledAnnualValue: 215,
    },

    complianceNote: '',
    requires: { legalReview: false, stateFiling: false, erisa: false },
    bulletPoints: [
      'Hospital admission cash benefits',
      'ER visit cash benefits',
      'Ambulance service cash benefits',
    ],
    color: 'red',
  },
  {
    id: 'dpc',
    order: 6,
    name: 'Direct Primary Care',
    shortDescription: 'Subscription-based unlimited primary care access.',
    longDescription:
      'Membership-model primary care. Delivers strong ROI through reduced ER/urgent care/specialist referrals. Coming soon — partnership in development.',
    icon: 'person-outline',
    defaultStatus: 'future',
    defaultEnabled: false,

    calculationMethod: 'per_employee_monthly',
    inputs: {
      monthlyContribution: 75,
      eligibleSource: 'section125_eligible',
      roiMultiplier: 3.0, // 3:1 ROI through reduced utilization
    },

    employerSavingsMethod: 'cost_avoidance_roi',
    employerSavingsParams: {
      roiMultiplier: 3.0,
      employerShare: 0.3,
    },
    employeeValueMethod: 'direct_benefit',
    employeeValueParams: {},

    complianceNote: 'Can be funded through Section 105 reimbursement.',
    requires: { legalReview: false, stateFiling: false, erisa: false },
    bulletPoints: [
      'Unlimited primary care visits',
      'Same-day and next-day appointments',
      'Direct physician access',
      'Integrated with labs and basic imaging',
    ],
    color: 'blue',
  },
  {
    id: 'pharmacy',
    order: 7,
    name: 'Pharmacy Optimization',
    shortDescription: '70+ commonly prescribed medications at no cost + discount Rx network.',
    longDescription:
      'Dramatically reduces employee Rx spend and provides secondary savings to employer through HSA/HRA reimbursement reductions.',
    icon: 'flask-outline',
    defaultStatus: 'active',
    defaultEnabled: true,

    calculationMethod: 'rx_spend_model',
    inputs: {
      avgAnnualRxSpendPerEmployee: 1200,
      savingsPercentage: 0.25, // midpoint of 15-40% range
      employerSharePercentage: 0.2, // share that flows to employer via HRA/HSA reduction
    },

    employerSavingsMethod: 'rx_employer_share',
    employerSavingsParams: {
      employerSharePercentage: 0.2,
    },
    employeeValueMethod: 'rx_employee_savings',
    employeeValueParams: {
      employeeSharePercentage: 0.8,
    },

    complianceNote: '',
    requires: { legalReview: false, stateFiling: false, erisa: false },
    bulletPoints: [
      '70+ commonly prescribed meds at $0 cost',
      'Discount Rx network for all others',
      'Home delivery available',
    ],
    color: 'green',
  },
  {
    id: 'wellness',
    order: 8,
    name: 'Wellness, Engagement & AI Navigation',
    shortDescription: 'Proactive engagement platform with measurable outcomes.',
    longDescription:
      'Coaching, biometric screening, wellness challenges, and AI-powered healthcare navigation. Generates documented healthcare engagement for compliance.',
    icon: 'pulse-outline',
    defaultStatus: 'active',
    defaultEnabled: true,

    calculationMethod: 'absenteeism_model',
    inputs: {
      absenteeismReductionDaysPerEmployee: 1.5,
      avgDailyLaborCost: 200, // default fallback; overridden by avgSalary/workdays when available
    },

    employerSavingsMethod: 'absenteeism_savings',
    employerSavingsParams: {
      absenteeismReductionDaysPerEmployee: 1.5,
      avgDailyLaborCost: 200,
    },
    employeeValueMethod: 'engagement_value',
    employeeValueParams: {
      perEmployeeAnnualValue: 0, // engagement is captured qualitatively by default
    },

    complianceNote: '',
    requires: { legalReview: false, stateFiling: false, erisa: false },
    bulletPoints: [
      'Health coaching & SMART goals',
      'DNA & biometric screening',
      'Activity tracking & challenges',
      'Preventive care reminders',
      'AI healthcare navigation',
      'Behavioral health engagement',
      'Family health management',
    ],
    color: 'purple',
  },
];

// IBOS layer status options
export const IBOS_STATUSES = {
  active: { label: 'Active', icon: 'checkmark-circle', color: 'green', description: 'Included in current program' },
  available: { label: 'Available', icon: 'flash-outline', color: 'blue', description: 'Can be added to this employer' },
  future: { label: 'Future', icon: 'time-outline', color: 'textLight', description: 'Coming soon / requires partnership' },
  na: { label: 'N/A', icon: 'close-circle-outline', color: 'textLight', description: 'Not applicable to this employer' },
};

// Calculation method registry — used by the Admin editor
export const IBOS_CALCULATION_METHODS = [
  { id: 'computed_from_section125', label: 'Computed from Section 125 analysis (Layer 1 only)' },
  { id: 'per_employee_monthly', label: 'Per-employee monthly contribution' },
  { id: 'utilization_model', label: 'Utilization / cost-avoidance model' },
  { id: 'from_plan_tiers', label: 'From Section 125 plan tiers' },
  { id: 'rx_spend_model', label: 'Pharmacy spend model' },
  { id: 'absenteeism_model', label: 'Absenteeism reduction model' },
];
